interface HeightInputProps {
  value:
    | {
        heightCm?: string;
        heightFeet?: string;
        heightInches?: string;
      }
    | string;
  onChange: (value: {
    heightCm?: string;
    heightFeet?: string;
    heightInches?: string;
  }) => void;
  measurementSystem: 'metric' | 'imperial';
  error?: string;
  disabled?: boolean;
  required?: boolean;
}

/**
 * HeightInput Component
 *
 * Specialized input for height that handles metric (cm) and imperial (ft/in) measurements.
 */
export function HeightInput({
  value,
  onChange,
  measurementSystem,
  error,
  disabled = false,
  required = false,
}: HeightInputProps) {
  // Normalize value to an object
  const normalizedValue =
    typeof value === 'string'
      ? { heightCm: value, heightFeet: '', heightInches: '' }
      : value;

  const heightCm = normalizedValue.heightCm || '';
  const heightFeet = normalizedValue.heightFeet || '';
  const heightInches = normalizedValue.heightInches || '';

  const baseInputClasses =
    'w-full px-4 py-2 border border-gray-200 rounded-lg bg-white disabled:bg-beige disabled:text-text-secondary focus:outline-none focus:ring-2 focus:ring-primary';

  return (
    <div>
      <label className="block text-sm font-medium mb-2 text-text-dark">
        Height
        {required && ' *'}
      </label>
      {measurementSystem === 'metric' ? (
        <input
          type="number"
          value={heightCm}
          onChange={(e) =>
            onChange({
              heightCm: e.target.value,
              heightFeet,
              heightInches,
            })
          }
          disabled={disabled}
          className={baseInputClasses}
          placeholder="Height in cm"
          min="50"
          max="300"
        />
      ) : (
        <div className="flex gap-2">
          <input
            type="number"
            value={heightFeet}
            onChange={(e) =>
              onChange({
                heightCm,
                heightFeet: e.target.value,
                heightInches,
              })
            }
            disabled={disabled}
            className={baseInputClasses}
            placeholder="Feet"
            min="1"
            max="9"
          />
          <input
            type="number"
            value={heightInches}
            onChange={(e) =>
              onChange({
                heightCm,
                heightFeet,
                heightInches: e.target.value,
              })
            }
            disabled={disabled}
            className={baseInputClasses}
            placeholder="Inches"
            min="0"
            max="11"
          />
        </div>
      )}
      {error && <p className="text-red-600 text-sm mt-1">{error}</p>}
    </div>
  );
}
